import React, { lazy, Suspense, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Navigate, Routes, Route, useLocation } from "react-router-dom";
import Home from "./pages/Home";
import Cursor from "./components/Cursor";
import SplashScreen from "./components/SplashScreen";
import SEOHead from "./components/SEOHead";
import { SiteDataProvider } from "./context/SiteDataContext";
import { Toaster } from "./components/ui/sonner";
import { isAdminAuthenticated } from "./lib/auth";

// Lazy-load all non-home routes to reduce initial bundle
const About = lazy(() => import("./pages/About"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const JournalistPortfolio = lazy(() => import("./pages/JournalistPortfolio"));
const FashionJournalist = lazy(() => import("./pages/FashionJournalist"));
const MusicJournalist = lazy(() => import("./pages/MusicJournalist"));
const CulturalCommentary = lazy(() => import("./pages/CulturalCommentary"));
const Interviews = lazy(() => import("./pages/Interviews"));
const Contact = lazy(() => import("./pages/Contact"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminForgotPassword = lazy(() => import("./pages/admin/AdminForgotPassword"));
const AdminResetPassword = lazy(() => import("./pages/admin/AdminResetPassword"));

const PageLoader = () => (
  <div className="min-h-screen bg-[var(--paper)] grid place-items-center">
    <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--ink-soft)] animate-pulse">
      Loading...
    </span>
  </div>
);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

const ProtectedAdminRoute = ({ children }) => {
  if (!isAdminAuthenticated()) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
};

const AdminLoginRoute = () => {
  if (isAdminAuthenticated()) {
    return <Navigate to="/admin" replace />;
  }
  return <AdminLogin />;
};

function App() {
  return (
    <div className="App">
      <SplashScreen />
      <BrowserRouter>
        <SiteDataProvider>
          <SEOHead />
          <Cursor />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/articles/:slug" element={<ArticleDetail />} />
              <Route path="/journalist-portfolio" element={<JournalistPortfolio />} />
              <Route path="/fashion-journalist" element={<FashionJournalist />} />
              <Route path="/music-journalist" element={<MusicJournalist />} />
              <Route path="/cultural-commentary" element={<CulturalCommentary />} />
              <Route path="/interviews" element={<Interviews />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/admin/login" element={<AdminLoginRoute />} />
              <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
              <Route path="/admin/reset-password" element={<AdminResetPassword />} />
              <Route
                path="/admin"
                element={
                  <ProtectedAdminRoute>
                    <AdminDashboard />
                  </ProtectedAdminRoute>
                }
              />
            </Routes>
          </Suspense>
          <Toaster position="bottom-right" />
        </SiteDataProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
